import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { analytic } from '../utils/Analytics.js';
import {
    VOICE_CONNECTING,
    VOICE_HANGED_UP,
    VOICE_LISTENING,
    VOICE_PAUSED,
    VOICE_SPEAKING,
    VOICE_THINKING,
} from '../utils/voiceStatus.js';

const WS_URL_DEFAULT = `wss://${process.env.REACT_APP_WEBSOCKET_VOICE_URL}/ws_voice_widget`;
const PLAYBACK_SAMPLE_RATE = 24000;

const TOAST_ERROR_STYLE = {
    position: 'top-right',
    style: {
        backgroundColor: 'white',
        color: 'red',
        fontSize: '13px',
        padding: '5px 7px',
        fontWeight: '500',
    },
};

const useRealtimeVoice = ({
    botId,
    chatId,
    languages,
    messages,
    setMessages,
    wsUrl = WS_URL_DEFAULT,
    SAMPLE_RATE = PLAYBACK_SAMPLE_RATE,
}) => {
    const { i18n, t } = useTranslation();

    // ── State ──

    const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);
    const [voiceMode, setVoiceMode] = useState(false);
    const [isMicMuted, setIsMicMuted] = useState(true);
    const [isSpeakerMuted, setIsSpeakerMuted] = useState(true);
    const [isAudioInteractionsDisabled, setIsAudioInteractionsDisabled] = useState(true);
    const [audioLevel, setAudioLevel] = useState(0);
    const [voiceErr, setVoiceErr] = useState(false);
    const [hangedUp, setHangedUp] = useState(false);
    const [transcriptDelta, setTranscriptDelta] = useState('');

    // ── Refs ──

    const voiceStatus = useRef(VOICE_CONNECTING);
    const wsRef = useRef(null);
    const audioStatusRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const playAudioRef = useRef(false);
    const audioContextRef = useRef(null);
    const gainNodeRef = useRef(null);
    const animationFrameRef = useRef(null);
    const isMicMutedRef = useRef(true);
    const isWelcomeMessageRequested = useRef(false);
    const activeSourcesRef = useRef([]);
    const nextPlayTimeRef = useRef(0);
    const playbackContextRef = useRef(null);
    const audioThresholdRef = useRef(null);
    const audioQueueRef = useRef([]);
    const voiceModeRef = useRef(false);
    const hangedUpRef = useRef(false);
    const messagesRef = useRef(messages);

    // Keep refs in sync with state so WebSocket callbacks see current values
    voiceModeRef.current = voiceMode;
    hangedUpRef.current = hangedUp;
    messagesRef.current = messages;

    // ── Cleanup on unmount ──

    useEffect(() => {
        return () => {
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (_) { /* noop */ }
                wsRef.current = null;
            }
            handleCloseAudio();
        };
    }, []);

    // ── WebSocket helpers ──

    const sendWsMessage = (msg) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(msg));
        }
    };

    // ── PCM16 conversion ──

    const pcm16ToAudioBuffer = (base64Data) => {
        const ctx = playbackContextRef.current;
        if (!ctx) return null;

        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const int16Array = new Int16Array(bytes.buffer);
        const float32Array = new Float32Array(int16Array.length);
        for (let i = 0; i < int16Array.length; i++) {
            float32Array[i] = int16Array[i] / 0x7FFF;
        }

        const audioBuffer = ctx.createBuffer(1, float32Array.length, SAMPLE_RATE);
        audioBuffer.copyToChannel(float32Array, 0);
        return audioBuffer;
    };

    // ── Audio teardown ──

    const handleCloseAudio = () => {
        if (audioStatusRef.current) {
            audioStatusRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        if (playbackContextRef.current) {
            playbackContextRef.current.close();
            playbackContextRef.current = null;
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        playAudioRef.current = false;

        if (gainNodeRef.current) {
            try { gainNodeRef.current.disconnect(); } catch (_) { /* noop */ }
            gainNodeRef.current = null;
        }

        for (const src of activeSourcesRef.current) {
            try { src.stop(); } catch (_) { /* noop */ }
            try { src.disconnect(); } catch (_) { /* noop */ }
        }
        activeSourcesRef.current = [];
        nextPlayTimeRef.current = 0;

        audioThresholdRef.current = null;
        audioQueueRef.current = [];
        setAudioLevel(0);
    };

    // ── Recording controls ──

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current = null;
            audioStatusRef.current = 'stopped';
        }
    };

    const pauseRecording = () => {
        if (
            (voiceStatus.current === VOICE_LISTENING ||
                voiceStatus.current === VOICE_THINKING ||
                voiceStatus.current === VOICE_SPEAKING) &&
            streamRef.current
        ) {
            const audioTrack = streamRef.current?.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = false;
            }
        }
    };

    const resumeRecording = () => {
        if (
            (voiceStatus.current === VOICE_PAUSED ||
                voiceStatus.current === VOICE_SPEAKING) &&
            streamRef.current
        ) {
            const audioTrack = streamRef.current?.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = true;
                if (!mediaRecorderRef.current && audioStatusRef.current === 'stopped') {
                    mediaRecorderRef.current = new MediaRecorder(streamRef.current);
                    mediaRecorderRef.current.start();
                    audioStatusRef.current = 'recording';
                }
            }
        }
    };

    // ── Interruption (clears playback, resets to listening) ──

    const handleInterruption = () => {
        for (const src of activeSourcesRef.current) {
            try { src.stop(); } catch (_) { /* noop */ }
            try { src.disconnect(); } catch (_) { /* noop */ }
        }
        activeSourcesRef.current = [];

        if (gainNodeRef.current) {
            try { gainNodeRef.current.disconnect(); } catch (_) { /* noop */ }
            gainNodeRef.current = null;
        }

        audioQueueRef.current = [];
        playAudioRef.current = false;
        nextPlayTimeRef.current = 0;
        setTranscriptDelta('');

        voiceStatus.current = VOICE_LISTENING;
    };

    // ── Playback (gapless scheduling) ──

    const scheduleBufferPlayback = (buffer) => {
        const ctx = playbackContextRef.current;
        if (!ctx || hangedUpRef.current) return;

        const source = ctx.createBufferSource();
        source.buffer = buffer;

        if (!gainNodeRef.current) {
            gainNodeRef.current = ctx.createGain();
            gainNodeRef.current.connect(ctx.destination);
        }

        source.connect(gainNodeRef.current);

        const now = ctx.currentTime;
        const startTime = Math.max(now, nextPlayTimeRef.current);
        nextPlayTimeRef.current = startTime + buffer.duration;

        activeSourcesRef.current.push(source);
        playAudioRef.current = true;

        source.onended = () => {
            const idx = activeSourcesRef.current.indexOf(source);
            if (idx === -1) return;
            activeSourcesRef.current.splice(idx, 1);

            if (activeSourcesRef.current.length === 0) {
                playAudioRef.current = false;

                if (isWelcomeMessageRequested.current) {
                    isWelcomeMessageRequested.current = false;
                }

                setTranscriptDelta('');

                if (voiceModeRef.current && !isMicMutedRef.current) {
                    voiceStatus.current = VOICE_LISTENING;

                    if (streamRef.current) {
                        const audioTrack = streamRef.current.getAudioTracks()[0];
                        if (audioTrack) {
                            audioTrack.enabled = true;
                        }
                        audioStatusRef.current = 'recording';
                    }
                }
            }
        };

        source.start(startTime);
    };

    const handleResponseAudio = (base64Data) => {
        const audioBuffer = pcm16ToAudioBuffer(base64Data);
        if (!audioBuffer) return;

        const ctx = playbackContextRef.current;
        if (!ctx) return;

        if (!playAudioRef.current) {
            nextPlayTimeRef.current = ctx.currentTime + 0.05;
        }

        scheduleBufferPlayback(audioBuffer);
    };

    // ── Audio analyser (captures mic PCM16 and streams as `media` events) ──

    const audioAnalyser = async () => {
        let stream = streamRef.current;
        if (!stream) {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: SAMPLE_RATE,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
        }

        let audioContext = audioContextRef.current;
        if (!audioContext) {
            audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
            audioContextRef.current = audioContext;
        }

        try {
            await audioContext.audioWorklet.addModule('/audio/audio-processor.worklet.js');
        } catch (error) {
            return;
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

        function bufferToBase64(buffer) {
            const bytes = new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        }

        let smoothedAudioLevel = 0;
        const SMOOTHING_FACTOR = 0.9;

        workletNode.port.onmessage = (event) => {
            if (event.data.event === 'emit_audio') {
                const base64Audio = bufferToBase64(event.data.data);

                sendWsMessage({
                    event: 'media',
                    data: { audio_data: base64Audio },
                });

                const currentLevel = event.data.audioLevel;
                smoothedAudioLevel =
                    smoothedAudioLevel * SMOOTHING_FACTOR +
                    currentLevel * (1 - SMOOTHING_FACTOR);

                if (
                    smoothedAudioLevel > audioThresholdRef.current &&
                    voiceStatus.current === VOICE_SPEAKING
                ) {
                    handleInterruption();
                }
            }
        };

        source.connect(analyser);
        source.connect(workletNode);
        workletNode.connect(audioContext.destination);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateAudioLevel = () => {
            if (!analyser) return;

            if (voiceStatus.current === VOICE_LISTENING) {
                analyser.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < bufferLength; i++) {
                    sum += dataArray[i];
                }
                const average = sum / bufferLength;
                setAudioLevel(average);
            } else {
                setAudioLevel(0);
            }

            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };

        updateAudioLevel();
    };

    // ── Microphone capture start ──

    const startRecording = async () => {
        try {
            let stream = streamRef.current;
            if (!stream) {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        channelCount: 1,
                        sampleRate: SAMPLE_RATE,
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                });
                streamRef.current = stream;
            }

            if (!mediaRecorderRef.current) {
                mediaRecorderRef.current = new MediaRecorder(stream);
            }

            if (
                messagesRef.current.length === 1 &&
                messagesRef.current[0].role === 'assistant'
            ) {
                voiceStatus.current = VOICE_SPEAKING;
            } else {
                voiceStatus.current = VOICE_LISTENING;
            }

            audioStatusRef.current = 'recording';
            mediaRecorderRef.current.start();

            audioAnalyser();
        } catch (error) {
            setVoiceErr(true);
            setIsAudioInteractionsDisabled(true);
        }
    };

    // ── Ambient noise calibration ──

    const startCalibration = async () => {
        let stream = streamRef.current;
        if (!stream) {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: SAMPLE_RATE,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
        }

        let audioContext = audioContextRef.current;
        if (!audioContext) {
            audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
            audioContextRef.current = audioContext;
        }

        try {
            await audioContext.audioWorklet.addModule('/audio/audio-callibration.worklet.js');
        } catch (error) {
            return;
        }

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, 'audio-callibration');

        const samples = [];

        workletNode.port.onmessage = (event) => {
            if (event.data.event === 'audio_threshold') {
                const rms = event.data.data;
                samples.push(rms);

                if (samples.length >= 200) {
                    const mean = samples.reduce((sum, val) => sum + val, 0) / samples.length;
                    const audioThreshold = Math.max(0.05, Math.min(mean * 2.5, 0.35));
                    audioThresholdRef.current = audioThreshold;
                    workletNode.disconnect();
                    source.disconnect();
                }
            }
        };

        source.connect(workletNode);
        workletNode.connect(audioContext.destination);
    };

    // ── Welcome message ──

    const playWelcomeMessage = () => {
        sendWsMessage({ event: 'play_welcome_message', data: null });
        isWelcomeMessageRequested.current = true;
    };

    // ── Disconnect helpers ──

    const disconnectWebSocket = () => {
        if (wsRef.current) {
            sendWsMessage({ event: 'closed', data: null });
            try { wsRef.current.close(); } catch (_) { /* noop */ }
            wsRef.current = null;
        }
    };

    const disconnectVoiceMode = (status, sendClose = true) => {
        setIsSpeakerMuted(true);
        setIsMicMuted(true);
        isMicMutedRef.current = true;
        setIsAudioInteractionsDisabled(true);
        setVoiceErr(false);
        setTranscriptDelta('');

        voiceStatus.current = status || VOICE_CONNECTING;

        if (sendClose) {
            disconnectWebSocket();
        }

        stopRecording();
        handleCloseAudio();
    };

    const exitVoiceMode = () => {
        setVoiceMode(false);
        setHangedUp(false);
        disconnectVoiceMode();
        analytic('voice_mode_exit', null);
    };

    // Ref-stable version so WebSocket callbacks can call it without stale closures
    const exitVoiceModeRef = useRef(exitVoiceMode);
    exitVoiceModeRef.current = exitVoiceMode;

    // ── Incoming WebSocket message handler ──

    const handleWsMessage = (event) => {
        if (event.data === 'pong') {
            return;
        }

        let response;
        try {
            response = JSON.parse(event.data);
        } catch {
            return;
        }

        switch (response.event) {
            case 'connected': {
                startRecording();
                sendWsMessage({ event: 'user_interruption', data: { enabled: true } });

                if (
                    messagesRef.current.length === 1 &&
                    messagesRef.current[0].role === 'assistant'
                ) {
                    playWelcomeMessage();
                }

                setIsMicMuted(false);
                isMicMutedRef.current = false;
                setIsSpeakerMuted(false);
                setIsAudioInteractionsDisabled(false);

                analytic('voice_mode_initiated', null);
                break;
            }

            case 'response_audio': {
                // Thinking mode skipped — transition directly from listening to speaking
                // if (voiceStatus.current === VOICE_THINKING) {
                //   audioQueueRef.current = [];
                // }

                if (voiceStatus.current !== VOICE_PAUSED && voiceStatus.current !== VOICE_SPEAKING) {
                    voiceStatus.current = VOICE_SPEAKING;

                    if (!isWelcomeMessageRequested.current) {
                        sendWsMessage({ event: 'audio_unmute', data: null });
                    }
                }

                if (!playbackContextRef.current) {
                    playbackContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
                }

                handleResponseAudio(response.data);
                break;
            }

            case 'response_done': {
                break;
            }

            case 'transcript_delta': {
                setTranscriptDelta((prev) => prev + (response.data || ''));
                break;
            }

            case 'transcript_done': {
                // Ignore when only message is assistant (welcome message)
                const msgs = messagesRef.current;
                const isWelcomeOnly = msgs.length === 1 && msgs[0]?.role === 'assistant';
                if (isWelcomeOnly) {
                    setTranscriptDelta('');
                    break;
                }
                const fullTranscript = response.data;
                if (fullTranscript) {
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        {
                            role: 'assistant',
                            content: fullTranscript,
                            is_flagged: 0,
                            reason: '',
                            messaged_at: Date.now(),
                        },
                    ]);
                }
                setTranscriptDelta('');
                break;
            }

            case 'user_transcript': {
                // Thinking mode skipped — stay in current state until response_audio transitions to speaking
                audioStatusRef.current = 'stopped';

                setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                        role: 'user',
                        content: response.data,
                        is_flagged: 0,
                        reason: '',
                        messaged_at: Date.now(),
                    },
                ]);
                break;
            }

            case 'welcome_failed': {
                voiceStatus.current = VOICE_LISTENING;
                break;
            }

            case 'interrupt': {
                handleInterruption();
                break;
            }

            case 'error': {
                toast.error(
                    response?.data || t('Something broke! Please try again later'),
                    TOAST_ERROR_STYLE,
                );
                exitVoiceModeRef.current();
                break;
            }

            default:
                break;
        }
    };

    // Stable ref so the WebSocket always calls the latest handler
    const handleWsMessageRef = useRef(handleWsMessage);
    handleWsMessageRef.current = handleWsMessage;

    // ── WebSocket connection ──

    const connectWebSocket = () => {
        if (wsRef.current) {
            try { wsRef.current.close(); } catch (_) { /* noop */ }
        }

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            const language = languages.find((lng) => lng.value === i18n.language);

            sendWsMessage({
                event: 'start',
                data: {
                    bot_id: botId,
                    chat_id: chatId,
                    language: language,
                },
            });
        };

        ws.onmessage = (event) => handleWsMessageRef.current(event);

        ws.onerror = (event) => {
            if (voiceModeRef.current) {
                exitVoiceModeRef.current();
            }
        };

        ws.onclose = () => {
            wsRef.current = null;
        };
    };

    const connectVoiceMode = () => {
        voiceStatus.current = VOICE_CONNECTING;
        connectWebSocket();
        startCalibration();
    };

    // ── Public API ──

    const openVoiceMode = () => {
        setAudioLevel(0);
        setVoiceMode(true);
        connectVoiceMode();
    };

    const handleMicMuteToggle = () => {
        if (
            voiceStatus.current === VOICE_LISTENING ||
            voiceStatus.current === VOICE_THINKING ||
            voiceStatus.current === VOICE_SPEAKING
        ) {
            sendWsMessage({ event: 'audio_mute', data: null });

            setIsMicMuted(true);
            isMicMutedRef.current = true;

            if (gainNodeRef.current) {
                gainNodeRef.current.gain.value = 0;
            }

            voiceStatus.current = VOICE_PAUSED;
        } else if (voiceStatus.current === VOICE_PAUSED) {
            setIsMicMuted(false);
            isMicMutedRef.current = false;

            if (gainNodeRef.current) {
                gainNodeRef.current.gain.value = 1;
            }

            if (audioContextRef.current) {
                if (activeSourcesRef.current.length === 0 && !playAudioRef.current) {
                    voiceStatus.current = VOICE_LISTENING;

                    sendWsMessage({ event: 'audio_unmute', data: null });
                } else {
                    voiceStatus.current = VOICE_SPEAKING;
                }
            }
        }
    };

    const hangupVoice = () => {
        disconnectVoiceMode(VOICE_HANGED_UP);
        setHangedUp(true);
    };

    const reOpenVoice = () => {
        setHangedUp(false);
        voiceStatus.current = VOICE_CONNECTING;
        connectVoiceMode();
        analytic('voice_mode_reconnect', null);
    };

    // No-op for chat WebSocket; realtime uses its own connection
    const handleVoiceSocketEvent = () => false;

    return {
        voiceModeEnabled,
        setVoiceModeEnabled,
        voiceMode,
        voiceStatus: voiceStatus.current,
        isMicMuted,
        isSpeakerMuted,
        isAudioInteractionsDisabled,
        audioLevel,
        hangedUp,
        voiceErr,
        transcriptDelta,

        openVoiceMode,
        exitVoiceMode,
        handleMicMuteToggle,
        hangupVoice,
        reOpenVoice,
        handleVoiceSocketEvent,
    };
};

export default useRealtimeVoice;
